import React, { useState } from 'react';
import { db } from '../../utils/firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, serverTimestamp, query, where, writeBatch } from 'firebase/firestore';
import { Category } from '../../types';
import { Plus, Trash2, Edit2, Check, X, Loader2, Download } from 'lucide-react';

// Seed list used when no categories exist in Firestore yet.
// Mirrors the previously hardcoded dropdown in AdminInventory.
export const DEFAULT_CATEGORIES: { name: string; isApparel: boolean }[] = [
  { name: 'Popsicle', isApparel: false },
  { name: 'Cruiser', isApparel: false },
  { name: 'Nos88', isApparel: false },
  { name: 'Gothika', isApparel: false },
  { name: 'Pool', isApparel: false },
  { name: 'Shovel', isApparel: false },
  { name: 'Tee shirt', isApparel: true },
  { name: 'Sweatshirt', isApparel: true },
  { name: 'Hoodies', isApparel: true },
  { name: 'Bottoms', isApparel: true },
  { name: 'Accessories', isApparel: false },
];

interface CategoryManagerProps {
  categories: Category[];
  onCategoriesChange: () => void;
  onProductsChange?: () => void;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({ categories, onCategoriesChange, onProductsChange }) => {
  const [newName, setNewName] = useState('');
  const [newIsApparel, setNewIsApparel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editIsApparel, setEditIsApparel] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const nameExists = (name: string, excludeId?: string) =>
    categories.some(c => c.id !== excludeId && c.name.trim().toLowerCase() === name.trim().toLowerCase());

  const countProductsInCategory = async (name: string): Promise<number> => {
    const q = query(collection(db, 'products'), where('category', '==', name));
    const snap = await getDocs(q);
    return snap.size;
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) {
      alert('Please enter a category name.');
      return;
    }
    if (nameExists(name)) {
      alert(`A category named "${name}" already exists.`);
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, 'categories'), {
        name,
        isApparel: newIsApparel,
        sortOrder: categories.length,
        createdAt: serverTimestamp(),
      });
      setNewName('');
      setNewIsApparel(false);
      onCategoriesChange();
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Failed to add category. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const existing = new Set(categories.map(c => c.name.toLowerCase()));
      const toSeed = DEFAULT_CATEGORIES.filter(c => !existing.has(c.name.toLowerCase()));
      const batch = writeBatch(db);
      toSeed.forEach((cat, idx) => {
        const newRef = doc(collection(db, 'categories'));
        batch.set(newRef, {
          name: cat.name,
          isApparel: cat.isApparel,
          sortOrder: categories.length + idx,
          createdAt: serverTimestamp(),
        });
      });
      await batch.commit();
      onCategoriesChange();
    } catch (error) {
      console.error('Error importing default categories:', error);
      alert('Failed to import default categories. Check console for details.');
    } finally {
      setSeeding(false);
    }
  };

  const startEdit = (cat: Category) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditIsApparel(!!cat.isApparel);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditIsApparel(false);
  };

  const handleSaveEdit = async (cat: Category) => {
    const name = editName.trim();
    if (!name) {
      alert('Category name cannot be empty.');
      return;
    }
    if (nameExists(name, cat.id)) {
      alert(`A category named "${name}" already exists.`);
      return;
    }

    const renamed = name !== cat.name;
    let updateProducts = false;
    if (renamed) {
      const count = await countProductsInCategory(cat.name);
      if (count > 0) {
        updateProducts = window.confirm(
          `${count} product(s) currently use "${cat.name}". Update them to "${name}" as well?\n\nOK = rename category and update products\nCancel = rename category only (products keep the old name)`
        );
      }
    }

    setBusyId(cat.id);
    try {
      await updateDoc(doc(db, 'categories', cat.id), { name, isApparel: editIsApparel });

      if (renamed && updateProducts) {
        const q = query(collection(db, 'products'), where('category', '==', cat.name));
        const snap = await getDocs(q);
        const batch = writeBatch(db);
        snap.forEach(productDoc => {
          batch.update(productDoc.ref, { category: name });
        });
        await batch.commit();
        onProductsChange?.();
      }

      cancelEdit();
      onCategoriesChange();
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category. Check console for details.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (cat: Category) => {
    const count = await countProductsInCategory(cat.name);
    const warning = count > 0
      ? `${count} product(s) currently use "${cat.name}". They will keep this category name, but it will no longer appear in the dropdown.\n\nDelete anyway?`
      : `Delete category "${cat.name}"? This cannot be undone.`;
    if (!window.confirm(warning)) return;

    setBusyId(cat.id);
    try {
      await deleteDoc(doc(db, 'categories', cat.id));
      onCategoriesChange();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category. Check console for details.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white p-6 rounded-sm shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-3">
        <h3 className="font-heading text-lg">MANAGE CATEGORIES</h3>
        {categories.length === 0 && (
          <button
            type="button"
            onClick={handleSeedDefaults}
            disabled={seeding}
            className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-black disabled:opacity-50"
          >
            {seeding ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            IMPORT DEFAULTS
          </button>
        )}
      </div>

      {/* Category List */}
      <div className="divide-y divide-gray-100 mb-4">
        {categories.map((cat) => (
          <div key={cat.id} className="py-2 flex items-center gap-2">
            {editingId === cat.id ? (
              <>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 border border-gray-300 p-1.5 text-sm focus:border-black focus:outline-none"
                  autoFocus
                />
                <label className="flex items-center gap-1 text-[10px] font-bold text-gray-500 whitespace-nowrap cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editIsApparel}
                    onChange={(e) => setEditIsApparel(e.target.checked)}
                  />
                  APPAREL
                </label>
                <button
                  type="button"
                  onClick={() => handleSaveEdit(cat)}
                  disabled={busyId === cat.id}
                  className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                  title="Save"
                >
                  {busyId === cat.id ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                </button>
                <button type="button" onClick={cancelEdit} className="text-gray-400 hover:text-black p-1" title="Cancel">
                  <X size={16} />
                </button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm font-body">{cat.name}</span>
                {cat.isApparel && (
                  <span className="text-[9px] font-bold bg-gray-100 text-gray-500 px-1.5 py-0.5 tracking-wider">APPAREL</span>
                )}
                <button
                  type="button"
                  onClick={() => startEdit(cat)}
                  className="text-blue-500 hover:text-blue-700 p-1"
                  title="Edit Category"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(cat)}
                  disabled={busyId === cat.id}
                  className="text-red-400 hover:text-red-600 p-1 disabled:opacity-50"
                  title="Delete Category"
                >
                  {busyId === cat.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
              </>
            )}
          </div>
        ))}
        {categories.length === 0 && (
          <div className="py-6 text-center text-gray-400 text-xs font-body">
            No categories yet. Add one below or import the defaults.
          </div>
        )}
      </div>

      {/* Add New Category */}
      <div className="bg-gray-50 p-3 border border-gray-200 rounded-sm">
        <label className="block text-[10px] font-bold tracking-widest text-gray-500 mb-2 uppercase">Add New Category</label>
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder="Category name (e.g. Longboards)"
            className="flex-1 border border-gray-300 p-2 text-xs focus:border-black focus:outline-none bg-white"
          />
          <label className="flex items-center gap-1 text-[10px] font-bold text-gray-500 whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              checked={newIsApparel}
              onChange={(e) => setNewIsApparel(e.target.checked)}
            />
            APPAREL
          </label>
          <button
            type="button"
            onClick={handleAdd}
            disabled={saving}
            className="bg-black text-white text-xs px-4 py-2 hover:bg-gray-800 disabled:opacity-50 transition-colors uppercase tracking-wider font-bold flex items-center gap-1"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            ADD
          </button>
        </div>
        <p className="text-[10px] text-gray-400 mt-2">
          * Mark as APPAREL to enable size &amp; color variant options for products in this category.
        </p>
      </div>
    </div>
  );
};

export default CategoryManager;
